// src/pages/NavigationPage.tsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import backendApi from "../../services/backendApi";
import { inventoryApi, vendorApi } from "../../services/inventoryVendorApi";
import useModuleAccess from "../../hooks/useModuleAccess";
import { masterSheetService, bdDetailsService } from "./forms/sites/Axon/services/axonApiService";
import { alertsApi } from "./alerts/services/alertsApi";
import {
    FileEdit,
    UploadCloud,
    BellRing,
    BarChart3,
    Building2,
    X,
    Activity,
    ChevronRight,
    Package,
    Users,
    Shield,
    Lock,
    AlertTriangle,
    FileText,
    BookOpen,
    Menu,
    ChevronLeft,
    Wrench,
    Zap,
    TrendingUp,
    TrendingDown
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import '../../styles/navigation-page.css';
import { MODULE_ICONS } from '../../components/icons/ModuleIcons';
import { accentFor, accentHex } from '../../components/icons/moduleAccent';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ComposedChart,
    ReferenceLine
} from "recharts";

// Type definitions
interface NavigationOption {
    moduleKey: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    action: () => void;
    color: string;
    isNew?: boolean;
}

type RouteParams = {
    site: string;
};

interface SiteSummary {
    site_name?: string;
    site_code?: string;
}

const ChartViewport: React.FC<{ minHeight: number; children: (size: { width: number; height: number }) => React.ReactNode }> = ({ minHeight, children }) => {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;

        let rafId = 0;
        const check = () => {
            const rect = host.getBoundingClientRect();
            const nextWidth = Math.max(0, Math.floor(rect.width));
            const nextHeight = Math.max(0, Math.floor(rect.height));

            setSize((prev) => {
                if (prev.width === nextWidth && prev.height === nextHeight) {
                    return prev;
                }
                return { width: nextWidth, height: nextHeight };
            });
        };

        const scheduleCheck = () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(check);
        };

        scheduleCheck();

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(scheduleCheck);
            observer.observe(host);
            return () => {
                observer.disconnect();
                if (rafId) {
                    cancelAnimationFrame(rafId);
                }
            };
        }

        window.addEventListener('resize', scheduleCheck);
        return () => {
            window.removeEventListener('resize', scheduleCheck);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
        };
    }, []);

    const canRender = size.width > 0 && size.height > 0;

    return (
        <div ref={hostRef} className="np-chart" style={{ minHeight }}>
            {canRender ? children(size) : null}
        </div>
    );
};

/* Accent + hover behaviour is shared with MainSidebar via moduleAccent.ts so a
 * module looks identical in the grid and in the sidebar. */
const getColorClasses = (color: string) => {
    const a = accentFor(color);
    return {
        tile: a.tile,
        icon: a.icon,
        border: 'hover:border-[#8AC4E6]',
        shadow: 'shadow-[#0084CF]/10',
        text: a.text,
        hover: 'hover:bg-[#F2F8FC]/50',
        cardBg: 'bg-white',
        cardShadow: 'hover:shadow-[0_10px_30px_rgba(0,132,207,0.10)]',
    };
};

const getAccentColor = (color: string) => accentHex(color);

interface CardShellProps {
    option: NavigationOption;
    colSpan: string;
    delay: number;
    hoverIdx: number;
    children: React.ReactNode;
    className?: string;
    locked: boolean;
    onClick?: () => void;
}

const CardShell: React.FC<CardShellProps> = ({
    option,
    colSpan,
    delay,
    hoverIdx,
    children,
    className = '',
    locked,
    onClick,
}) => {
    const colors = getColorClasses(option.color);
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.45, delay }}
            whileHover={locked ? undefined : { y: -3, transition: { duration: 0.18 } }}
            whileTap={locked ? undefined : { scale: 0.99 }}
            onClick={onClick}
            role={locked ? undefined : 'button'}
            tabIndex={locked ? -1 : 0}
            aria-disabled={locked || undefined}
            aria-label={`${option.label} — ${option.description}`}
            onKeyDown={(e) => {
                if (locked || !onClick) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
            className={`np-card ${colSpan} ${className} group${locked ? ' is-locked' : ''}`}
        >
            <div className="np-card__accent" />

            <div className="np-card__head">
                <span className={`np-card__mark ${colors.tile} ${colors.icon}`}>
                    {React.cloneElement(option.icon as React.ReactElement<any>, { size: 18 })}
                </span>
                <span style={{ minWidth: 0 }}>
                    <span className="np-card__title">
                        {locked && <Lock size={11} style={{ flex: 'none', color: '#0066A3' }} />}
                        <span>{option.label}</span>
                    </span>
                    <span className="np-card__desc" style={{ display: 'block' }}>
                        {option.description}
                    </span>
                </span>
                {locked ? (
                    <span className="np-card__lock"><Lock size={9} /> Locked</span>
                ) : (
                    <ChevronRight size={15} strokeWidth={1.9} className="np-card__go" />
                )}
            </div>

            <div className="np-card__body">{children}</div>
        </motion.div>
    );
};

const BACKEND_BASE_URL = 'http://localhost:8000';
const DASHBOARD_AUTO_REFRESH_MS = 90000;
const DASHBOARD_RATE_LIMIT_BACKOFF_MS = 180000;

const NavigationPage: React.FC = () => {
    const { site } = useParams<RouteParams>();
    const navigate = useNavigate();
    const { user, logout, isGlobalAdmin } = useAuth();
    const [isValidating, setIsValidating] = useState(true);
    const [siteInfo, setSiteInfo] = useState<SiteSummary | null>(null);
    const [expandedReports, setExpandedReports] = useState(() => {
        try { return localStorage.getItem('nav_expanded_reports') === 'true'; } catch { return false; }
    });
    const [expandedDocumentation, setExpandedDocumentation] = useState(() => {
        try { return localStorage.getItem('nav_expanded_documentation') === 'true'; } catch { return false; }
    });

    useEffect(() => {
        try { localStorage.setItem('nav_expanded_reports', String(expandedReports)); } catch (e) {}
    }, [expandedReports]);

    useEffect(() => {
        try { localStorage.setItem('nav_expanded_documentation', String(expandedDocumentation)); } catch (e) {}
    }, [expandedDocumentation]);
    const [lockedModuleWarning, setLockedModuleWarning] = useState<string | null>(null);
    const { hasModuleAccess, loading: moduleAccessLoading, loadedFromApi, allowedModuleKeys } = useModuleAccess(site);

    // Metrics State
    const [generationMetrics, setGenerationMetrics] = useState({
        totalGeneration: 0,
        plantYield: 0,
        insolation: 0,
        pa: 0,
        revenue: 0,
        gaLoss: 0,
        infoTags: 0,
        totalDays: 0,
        powerVsRad: [] as Array<{ time: string; power: number; radiation: number }>,
        majorBreakdowns: [] as Array<{ name: string; value: number }>,
        controllableLosses: { value: 0, percentage: 0 },
        uncontrollableLosses: { weather: 0, grid: 0 },
        dateRange: ''
    });

    const [alertsMetrics, setAlertsMetrics] = useState({
        high: 0,
        medium: 0,
        low: 0,
        accurate: 0,
        inaccurate: 0,
        pending: 0,
        timeline: [
            { hour: '6am', alerts: 0 }, { hour: '8am', alerts: 0 }, { hour: '10am', alerts: 0 },
            { hour: '12pm', alerts: 0 }, { hour: '2pm', alerts: 0 }, { hour: '4pm', alerts: 0 }, { hour: '6pm', alerts: 0 },
        ]
    });

    const [inventoryMetrics, setInventoryMetrics] = useState({
        totalItems: 0,
        outOfStock: 0,
        highest3m: 'N/A',
        least3m: 'N/A',
        chart: [
            { cat: 'Modules', stock: 0 },
            { cat: 'Inv.', stock: 0 },
            { cat: 'Combiner', stock: 0 },
            { cat: 'Trans.', stock: 0 },
        ] as Array<{ cat: string; stock: number }>
    });

    const [vendorMetrics, setVendorMetrics] = useState({
        registeredVendors: 0,
        posIssued: 0,
        totalPoValueCr: 0,
        amountDueCr: 0,
        performance: [
            { vendor: 'V-A', perf: 0 },
            { vendor: 'V-B', perf: 0 },
            { vendor: 'V-C', perf: 0 },
            { vendor: 'V-D', perf: 0 },
        ] as Array<{ vendor: string; perf: number }>
    });

    const [omMetrics, setOmMetrics] = useState({
        completedLastWeek: 0,
        scheduledNextWeek: 0,
        ptwClosed: 0,
        totalChecklists: 0,
        pendingActivities: [] as Array<{ name: string; date: string }>
    });
    const [rtcGraphData, setRtcGraphData] = useState<any[]>([]);
    
    const moduleMetricsInFlightRef = useRef(false);
    const moduleMetricsBackoffUntilRef = useRef(0);

    const isRateLimitSignal = (value: any): boolean => {
        if (!value) return false;
        const status = Number(value?.status);
        if (status === 429) return true;
        const message = String(value?.error || value?.message || value?.detail || value || '').toLowerCase();
        return message.includes('429') || message.includes('too many requests') || message.includes('rate limit');
    };

    const toFiniteNumber = (...values: any[]): number => {
        for (const raw of values) {
            if (raw === null || raw === undefined || raw === '') continue;
            if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
            if (typeof raw === 'string') {
                const cleaned = raw.replace(/,/g, '').trim();
                if (!cleaned) continue;
                const parsed = Number(cleaned);
                if (Number.isFinite(parsed)) return parsed;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed)) return parsed;
        }
        return 0;
    };

    // Improved validation: 0 is a valid metric value, show it properly
    const hasMetricValue = (value: any): boolean => {
        const num = Number(value);
        return Number.isFinite(num);
    };

    // Format metric value with fallback to 0 (not "—")
    const formatMetricValue = (value: any, decimals: number = 1, unit: string = ''): string => {
        const num = Number(value);
        if (!Number.isFinite(num)) {
            return `0${unit}`; // Show 0 instead of "-"
        }
        return `${num.toFixed(decimals)}${unit}`;
    };

    const asRows = (payload: any): any[] => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.results)) return payload.results;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    };

    // Calculate Metrics on Mount
    useEffect(() => {
        const calculateMetrics = async () => {
            try {
                // 1. Fetch MasterSheet Data (Site-wide, last 30 days)
                // Use { userId: -1, siteCode: site } to load data for ALL users for the CURRENT SITE
                // This ensures we see data even if it's from last year or uploaded by another user
                const msResult = await masterSheetService.loadByUser({ userId: -1, siteCode: site });
                const msData = asRows(msResult?.data);

                if (msResult.ok && msData.length > 0) {
                    // Use ALL data as requested (Cumulative / Lifetime)

                    if (msData.length > 0) {
                        // Calculate Date Range
                        const sortedByDate = [...msData].sort((a: any, b: any) =>
                            new Date(a.entry_date).getTime() - new Date(b.entry_date).getTime()
                        );
                        const firstRecord = sortedByDate[0];
                        const lastRecord = sortedByDate[sortedByDate.length - 1];

                        const formatDate = (dateString: string) => {
                            const date = new Date(dateString);
                            // Format: ddMMMyy (e.g. 01Apr25)
                            const day = date.getDate().toString().padStart(2, '0');
                            const month = date.toLocaleString('default', { month: 'short' });
                            const year = date.getFullYear().toString().slice(-2);
                            return `${day}${month}${year}`;
                        };

                        const dateRangeStr = `${formatDate(firstRecord.entry_date)} - ${formatDate(lastRecord.entry_date)}`;

                        // 1. Total Generation = Sum of Net Energy / 1000
                        const totalNetEnergy = msData.reduce((sum: number, row: any) => {
                            let val = toFiniteNumber(row.net_energy_mwh, row.net_energy, row['Net Energy']);
                            // Fallback if net_energy is 0/missing but export/import exist
                            if (val === 0 && (row.export_mwh || row.import_mwh)) {
                                val = toFiniteNumber(row.export_mwh) - toFiniteNumber(row.import_mwh);
                            }
                            return sum + val;
                        }, 0);
                        const totalGen = totalNetEnergy / 1000;

                        // 2. Calculate Total Number of Days (unique dates in dataset)
                        const uniqueDates = new Set(msData.map((row: any) => {
                            const date = new Date(row.entry_date);
                            return date.toISOString().split('T')[0]; // Get YYYY-MM-DD format
                        }));
                        const totalDays = uniqueDates.size;

                        // 3. Plant Specific Yield = (Total Gen / DC Capacity) x 1000 / Total Days
                        // Total Gen is in MU, DC Capacity is 375 MWp
                        // Formula: (TotalGen_MU / 375_MWp) x 1000 / Days = kWh/kWp/day
                        const dcCapacity = 375; // MWp
                        const yieldVal = (totalGen > 0 && totalDays > 0)
                            ? ((totalGen / dcCapacity) * 1000) / totalDays
                            : 0;

                        // 4. Total Insolation = Total of GII
                        const totalInsolation = msData.reduce((sum: number, row: any) =>
                            sum + toFiniteNumber(row.gii_kwh_per_m2, row.poa_kwh_per_m2, row.gii, row['GII'], row['POA']), 0);

                        // 5. PA = Average of PA Energy
                        const paValues = msData
                            .map((row: any) => toFiniteNumber(row.plant_availability_percent, row.pa_energy, row.pa, row['PA']))
                            .filter((val: number) => Number.isFinite(val));
                        const totalPA = paValues.reduce((sum: number, value: number) => sum + value, 0);
                        const avgPA = paValues.length > 0 ? totalPA / paValues.length : 0;

                        // Fetch BD Details
                        const bdResult = await bdDetailsService.loadByUser({ userId: -1, siteCode: site });
                        let bdLoss = 0;
                        let majorBreakdownsArray: Array<{ name: string; value: number }> = [];
                        const formatShortDate = (dateString: string) => {
                            const d = new Date(dateString);
                            if (Number.isNaN(d.getTime())) return dateString;
                            return `${d.getDate()}/${d.getMonth() + 1}`;
                        };

                        const recentRows = [...msData]
                            .sort((a: any, b: any) => new Date(a.entry_date).getTime() - new Date(b.entry_date).getTime())
                            .slice(-7);

                        const powerVsRadData = recentRows.length > 0
                            ? recentRows.map((row: any) => ({
                                time: formatShortDate(row.entry_date),
                                power: toFiniteNumber(row.net_energy_mwh, row.net_energy, row['Net Energy']),
                                radiation: toFiniteNumber(row.gii_kwh_per_m2, row.poa_kwh_per_m2, row.gii, row['GII'], row['POA'])
                            }))
                            : [
                                { time: 'N/A', power: 0, radiation: 0 },
                            ];

                        const bdData = asRows(bdResult?.data);
                        if (bdResult.ok && bdData.length > 0) {
                            // Use ALL data

                            // 5. Controllable Losses = Total Gen Loss / 1 Million
                            const totalGenLossKwh = bdData.reduce((sum: number, row: any) =>
                                sum + toFiniteNumber(row.generation_loss_kwh, row['Generation loss kWh'], row['Gen Loss']), 0);

                            bdLoss = totalGenLossKwh / 1000000;

                            // 6. Major Breakdowns = Top 3 categories by occurrence -> individual values
                            if (bdData.length > 0) {
                                const categoryCounts: Record<string, number> = {};
                                const categoryLosses: Record<string, number> = {};

                                bdData.forEach((row: any) => {
                                    const cat = row.bd_category || row['BD Category'] || row.category || row.equipment || row['Equipment'] || 'Unknown';
                                    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                                    categoryLosses[cat] = (categoryLosses[cat] || 0) + toFiniteNumber(row.generation_loss_kwh, row['Generation loss kWh'], row['Gen Loss']);
                                });

                                // Find Top 3 Categories by occurrence
                                const top3Categories = Object.entries(categoryCounts)
                                    .sort(([, a], [, b]) => b - a)
                                    .slice(0, 3)
                                    .map(([cat]) => cat);

                                // Major breakdown widget should show category occurrence counts.
                                majorBreakdownsArray = top3Categories.map(cat => ({
                                    name: cat,
                                    value: categoryCounts[cat] || 0
                                }));
                            }
                        }

                        const bdLossPercentage = totalGen > 0 ? ((bdLoss / totalGen) * 100) : 0;

                        // GA Loss = Grid Downtime Energy Loss / 1000 (MU)
                        let gaLossVal = 0;
                        if (bdResult.ok && bdData.length > 0) {
                            gaLossVal = bdData.reduce((sum: number, row: any) =>
                                sum + toFiniteNumber(
                                    row.grid_downtime_energy_loss ||
                                    row['Grid Downtime Energy Loss'] ||
                                    row['Grid Loss'] ||
                                    row.grid_loss || 0
                                ), 0) / 1000;
                        }

                        // Revenue = Total Gen (MU) * Tariff / 10  (Crores, assuming tariff ~4.5 INR/kWh)
                        const TARIFF = 4.5;
                        const revenueVal = totalGen * TARIFF / 10;

                        const newMetrics = {
                            totalGeneration: totalGen,
                            plantYield: yieldVal,
                            insolation: totalInsolation,
                            pa: avgPA,
                            revenue: revenueVal,
                            gaLoss: gaLossVal,
                            infoTags: msData.length + bdData.length,
                            totalDays: totalDays,
                            powerVsRad: powerVsRadData,
                            majorBreakdowns: majorBreakdownsArray,
                            controllableLosses: { value: bdLoss, percentage: bdLossPercentage },
                            uncontrollableLosses: { weather: 0, grid: gaLossVal },
                            dateRange: dateRangeStr
                        };
                        setGenerationMetrics(newMetrics);
                    } else {
                        // No MasterSheet data — leave metrics at zero defaults
                    }
                } else {
                    // API returned empty result — leave metrics at zero defaults
                }
            } catch (error) {
                console.error("calculateMetrics failed", error);
            }
        };

        if (user && !isValidating) {
            calculateMetrics();
            const interval = setInterval(() => {
                if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                    return;
                }
                calculateMetrics();
            }, DASHBOARD_AUTO_REFRESH_MS);
            return () => clearInterval(interval);
        }
    }, [user, isValidating, site]);

    useEffect(() => {
        let isMounted = true;

        const loadModuleMetrics = async () => {
            if (!site) return;
            if (moduleMetricsInFlightRef.current) return;

            const now = Date.now();
            if (now < moduleMetricsBackoffUntilRef.current) {
                return;
            }

            moduleMetricsInFlightRef.current = true;

            try {
                const alertStats = await alertsApi.fetchStats(site).catch((e: any) => ({ __error: e }));
                await new Promise((resolve) => setTimeout(resolve, 120));

                const alertList = await alertsApi.fetchAlerts(site).catch((e: any) => ({ __error: e }));
                await new Promise((resolve) => setTimeout(resolve, 120));

                const invStatsResp = await inventoryApi.getStats(site);
                await new Promise((resolve) => setTimeout(resolve, 120));

                const invListResp = await inventoryApi.getAll(site);
                await new Promise((resolve) => setTimeout(resolve, 120));

                const vendorStatsResp = await vendorApi.getStats(site);

                const hitRateLimit =
                    isRateLimitSignal((alertStats as any)?.__error || alertStats) ||
                    isRateLimitSignal((alertList as any)?.__error || alertList) ||
                    isRateLimitSignal(invStatsResp) ||
                    isRateLimitSignal(invListResp) ||
                    isRateLimitSignal(vendorStatsResp);

                if (hitRateLimit) {
                    moduleMetricsBackoffUntilRef.current = Date.now() + DASHBOARD_RATE_LIMIT_BACKOFF_MS;
                    return;
                }

                const alertStatsSafe = alertStats || ({} as any);
                const alertRows = asRows((alertList as any)?.alerts || alertList);
                const timelineBuckets: Record<string, number> = {
                    '6am': 0, '8am': 0, '10am': 0, '12pm': 0, '2pm': 0, '4pm': 0, '6pm': 0,
                };
                alertRows.forEach((a: any) => {
                    const hour = Number(a?.alert_hour);
                    if (!Number.isFinite(hour)) return;
                    if (hour >= 6 && hour < 8) timelineBuckets['6am'] += 1;
                    else if (hour >= 8 && hour < 10) timelineBuckets['8am'] += 1;
                    else if (hour >= 10 && hour < 12) timelineBuckets['10am'] += 1;
                    else if (hour >= 12 && hour < 14) timelineBuckets['12pm'] += 1;
                    else if (hour >= 14 && hour < 16) timelineBuckets['2pm'] += 1;
                    else if (hour >= 16 && hour < 18) timelineBuckets['4pm'] += 1;
                    else if (hour >= 18 && hour < 20) timelineBuckets['6pm'] += 1;
                });

                const invStats = invStatsResp?.data || {};
                const invRows = asRows(invListResp?.data);
                const consumptionSorted = [...invRows]
                    .map((row: any) => ({
                        name: String(row?.equipment_name || row?.item_name || row?.category || 'N/A'),
                        c3m: toFiniteNumber(row?.consumption_pattern_last_three_months, row?.consumption3m),
                        stock: toFiniteNumber(row?.spare_availability, row?.quantity),
                        cat: String(row?.category || 'Other')
                    }))
                    .filter((row: any) => row.name && row.name !== 'N/A');

                const highest3m = consumptionSorted.length
                    ? [...consumptionSorted].sort((a, b) => b.c3m - a.c3m)[0]?.name || 'N/A'
                    : 'N/A';
                const least3m = consumptionSorted.length
                    ? [...consumptionSorted].sort((a, b) => a.c3m - b.c3m)[0]?.name || 'N/A'
                    : 'N/A';

                const categoryStockMap: Record<string, number> = {};
                consumptionSorted.forEach((r: any) => {
                    categoryStockMap[r.cat] = (categoryStockMap[r.cat] || 0) + r.stock;
                });
                const invChart = Object.entries(categoryStockMap)
                    .sort(([, a], [, b]) => Number(b) - Number(a))
                    .slice(0, 4)
                    .map(([cat, stock]) => ({ cat: cat.length > 8 ? `${cat.slice(0, 8)}.` : cat, stock: Number(stock) }));

                while (invChart.length < 4) {
                    invChart.push({ cat: 'N/A', stock: 0 });
                }

                const vendorStats = vendorStatsResp?.data || {};
                const totalValue = toFiniteNumber(vendorStats.totalValue);
                const totalInvoiced = toFiniteNumber(vendorStats.totalInvoicedValue);
                const amountDue = Math.max(totalValue - totalInvoiced, 0);
                const active = toFiniteNumber(vendorStats.active);
                const pendingRenewal = toFiniteNumber(vendorStats.pendingRenewal);
                const inactive = toFiniteNumber(vendorStats.inactive);
                const totalVendors = toFiniteNumber(vendorStats.total);
                const toPct = (v: number) => totalVendors > 0 ? Math.min(Math.round((v / totalVendors) * 100), 100) : 0;

                const token = sessionStorage.getItem('access_token') || '';
                const omHeaders: Record<string, string> = {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    ...(site ? { 'X-Site-Code': site } : {}),
                };

                let omStats: any = {};
                let omRows: any[] = [];
                try {
                    const [omStatsRes, omEventsRes] = await Promise.all([
                        fetch(`${BACKEND_BASE_URL}/api/om/pm-events/stats/`, { headers: omHeaders }),
                        fetch(`${BACKEND_BASE_URL}/api/om/pm-events/?site_code=${encodeURIComponent(site)}&page_size=100`, { headers: omHeaders }),
                    ]);
                    omStats = omStatsRes.ok ? await omStatsRes.json() : {};
                    const omPayload = omEventsRes.ok ? await omEventsRes.json() : [];
                    omRows = asRows(omPayload);
                } catch {
                    omStats = {};
                    omRows = [];
                }

                const pendingActivities = omRows
                    .filter((row: any) => ['scheduled', 'in-progress', 'overdue'].includes(String(row?.status || '')))
                    .sort((a: any, b: any) => new Date(a?.date || '').getTime() - new Date(b?.date || '').getTime())
                    .slice(0, 3)
                    .map((row: any) => {
                        const d = new Date(row?.date || '');
                        const dd = Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                        return {
                            name: String(row?.title || row?.task_type || 'PM Activity'),
                            date: dd,
                        };
                    });

                if (!isMounted) return;

                setAlertsMetrics({
                    high: toFiniteNumber((alertStatsSafe as any).high_priority),
                    medium: toFiniteNumber((alertStatsSafe as any).medium_priority),
                    low: toFiniteNumber((alertStatsSafe as any).low_priority),
                    accurate: toFiniteNumber((alertStatsSafe as any).accurate_alerts),
                    inaccurate: toFiniteNumber((alertStatsSafe as any).inaccurate_alerts),
                    pending: toFiniteNumber((alertStatsSafe as any).not_rectified_count),
                    timeline: [
                        { hour: '6am', alerts: timelineBuckets['6am'] },
                        { hour: '8am', alerts: timelineBuckets['8am'] },
                        { hour: '10am', alerts: timelineBuckets['10am'] },
                        { hour: '12pm', alerts: timelineBuckets['12pm'] },
                        { hour: '2pm', alerts: timelineBuckets['2pm'] },
                        { hour: '4pm', alerts: timelineBuckets['4pm'] },
                        { hour: '6pm', alerts: timelineBuckets['6pm'] },
                    ]
                });

                setInventoryMetrics({
                    totalItems: toFiniteNumber(invStats.totalItems),
                    outOfStock: toFiniteNumber(invStats.outOfStock),
                    highest3m,
                    least3m,
                    chart: invChart,
                });

                setVendorMetrics({
                    registeredVendors: toFiniteNumber(vendorStats.total),
                    posIssued: toFiniteNumber(vendorStats.total),
                    totalPoValueCr: totalValue / 10000000,
                    amountDueCr: amountDue / 10000000,
                    performance: [
                        { vendor: 'Active', perf: toPct(active) },
                        { vendor: 'Renew', perf: toPct(pendingRenewal) },
                        { vendor: 'Inactive', perf: toPct(inactive) },
                        { vendor: 'Coverage', perf: Math.min(toPct(active + pendingRenewal), 100) },
                    ]
                });

                setOmMetrics({
                    completedLastWeek: toFiniteNumber(omStats.completed),
                    scheduledNextWeek: toFiniteNumber(omStats.upcoming_7_days),
                    ptwClosed: toFiniteNumber(omStats.completed),
                    totalChecklists: toFiniteNumber(omStats.total),
                    pendingActivities,
                });
            } catch (error) {
                // Intentionally silent in production for non-critical dashboard widgets.
                if (isRateLimitSignal(error)) {
                    moduleMetricsBackoffUntilRef.current = Date.now() + DASHBOARD_RATE_LIMIT_BACKOFF_MS;
                }
            } finally {
                moduleMetricsInFlightRef.current = false;
            }
        };

        if (user && !isValidating) {
            loadModuleMetrics();
            const interval = setInterval(() => {
                if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                    return;
                }
                loadModuleMetrics();
            }, DASHBOARD_AUTO_REFRESH_MS);
            return () => {
                isMounted = false;
                clearInterval(interval);
            };
        }

        return () => {
            isMounted = false;
        };
    }, [user, isValidating, site]);

    // Check if user is admin
    const roleNormalized = (user?.role || '').toLowerCase().replace(' ', '_');
    const roleNameNormalized = (user?.roleName || '').toLowerCase().replace(' ', '_');
    const isAdmin = isGlobalAdmin ||
        ['admin', 'super_admin', 'system_admin', 'company_admin', 'ho_admin', 'site_admin', 'ho'].includes(roleNormalized) ||
        ['admin', 'super_admin', 'system_admin', 'company_admin', 'ho_admin', 'site_admin', 'ho'].includes(roleNameNormalized);

    // Validate user exists in database on mount (only once)
    useEffect(() => {
        const validateUser = async () => {
            const token = sessionStorage.getItem('access_token');

            if (!token || !user) {
                setIsValidating(false);
                navigate('/login', { replace: true });
                return;
            }

            // Only validate once on mount - AuthContext handles periodic validation
            try {
                const response = await backendApi.getCurrentUser();

                if (response.success && response.user) {
                    // User exists and is valid
                    setIsValidating(false);
                } else {
                    // User doesn't exist - logout and redirect
                    logout();
                    navigate('/login', { replace: true });
                }
            } catch (error: any) {
                // Handle connection errors gracefully
                if (error.message === 'SERVER_NOT_AVAILABLE' || error.code === 'CONNECTION_REFUSED') {
                    // If server is down, allow access (offline mode)
                    setIsValidating(false);
                } else {
                    // For authentication errors, check if it's a real auth failure
                    // Only logout on clear authentication failures, not on temporary issues
                    const errorMsg = error.message?.toLowerCase() || '';
                    if (errorMsg.includes('unauthorized') || errorMsg.includes('authentication')) {
                        logout();
                        navigate('/login', { replace: true });
                    } else {
                        // Allow access on other errors (might be temporary)
                        setIsValidating(false);
                    }
                }
            }
        };

        validateUser();
    }, []); // Only run once on mount

    // Ensure URL site matches user's actual site/tenant
    useEffect(() => {
        if (user && !isValidating) {
            const urlSite = site?.toUpperCase();

            // For admins, we favor the current URL site if it exists.
            // For regular users, we force them to their assigned siteCode/tenant.
            const userSite = (
                (isAdmin && urlSite ? urlSite : (user.siteCode || user.tenant)) ||
                localStorage.getItem('currentSite') ||
                site ||
                ''
            ).toUpperCase();

            // If URL site doesn't match the determined userSite, redirect to correct URL
            if (userSite && urlSite !== userSite) {
                navigate(`/navigate/${userSite}`, { replace: true });
            }
        }
    }, [site, user, navigate, isValidating, isAdmin]);

    useEffect(() => {
        let mounted = true;
        const fetchRtcData = async () => {
            if (!site) return;
            try {
                const token = sessionStorage.getItem('access_token') || localStorage.getItem('token');
                if (!token) return;
                const siteCode = site.toUpperCase();
                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'X-Site-Code': siteCode,
                };

                let targetRunId = '';
                const histRes = await fetch(`${BACKEND_BASE_URL}/api/v1/rtc-simulation/history/?site_code=${siteCode}`, { headers });
                if (histRes.ok) {
                    const histData = await histRes.json();
                    const runs = histData?.runs || [];
                    const completed = runs.filter((r: any) => r.status === 'completed' || r.status === 'success');
                    if (completed.length > 0) {
                        targetRunId = completed[0].run_id;
                    }
                }

                const url = targetRunId
                    ? `${BACKEND_BASE_URL}/api/v1/rtc-simulation/graph-data/?run_id=${targetRunId}&site_code=${siteCode}`
                    : `${BACKEND_BASE_URL}/api/v1/rtc-simulation/graph-data/?site_code=${siteCode}`;

                const chartRes = await fetch(url, { headers });
                if (!chartRes.ok) return;
                const chartPayload = await chartRes.json();
                
                let data = [];
                if (Array.isArray(chartPayload)) {
                    data = chartPayload;
                } else if (chartPayload?.data) {
                    data = chartPayload.data;
                }
                
                if (data.length > 0) {
                    const MAX_POINTS = 48;
                    let sampledData = data;
                    if (data.length > MAX_POINTS) {
                        const step = Math.ceil(data.length / MAX_POINTS);
                        sampledData = data.filter((_: any, i: number) => i % step === 0);
                    }
                    
                    sampledData = sampledData.map((d: any) => {
                        let hourStr = '00';
                        if (d.Time) {
                            hourStr = String(d.Time).substring(0, 5);
                        } else if (d.Datetime) {
                            const dt = new Date(d.Datetime);
                            if (!isNaN(dt.getTime())) {
                                hourStr = dt.getHours().toString().padStart(2, '0') + ':' + dt.getMinutes().toString().padStart(2, '0');
                            } else {
                                const parts = String(d.Datetime).split(' ');
                                const timePart = parts.length > 1 ? parts[1] : parts[0];
                                hourStr = timePart.substring(0, 5);
                            }
                        } else if (d.hour) {
                            hourStr = String(d.hour);
                        }
                        return {
                            ...d,
                            hour: hourStr,
                            Grid_MW: Number(d.Grid_MW ?? d.Grid_Load_MWh ?? d.Grid_Load ?? d.Grid ?? 0),
                            Exchange_MW: Number(d.Exchange_MW ?? d.Exchange_Load_MWh ?? d.Exchange_Load ?? d.Exchange ?? 0),
                            Batt_C_D_MW: Number(d.Batt_C_D_MW ?? d.Battery_Charge_Discharge_Curr_Day_MWh ?? d.Batt_Power ?? d.Battery ?? 0),
                            Solar_Power_MW: Number(d.Solar_Power_MW ?? d.solar_gen ?? d.Solar ?? 0),
                            Wind_Power_MW: Number(d.Wind_Power_MW ?? d.wind_gen ?? d.Wind ?? 0),
                        };
                    });
                    
                    if (mounted) {
                        setRtcGraphData(sampledData);
                    }
                }
            } catch (e) {
                console.error("Failed to fetch RTC data for mini chart:", e);
            }
        };
        fetchRtcData();
        return () => { mounted = false; };
    }, [site]);

    useEffect(() => {
        let isMounted = true;

        const fetchSiteMeta = async () => {
            if (!site) return;
            try {
                const normalized = site.toUpperCase();
                const response = await backendApi.getSites(false, normalized);
                if (response.success && Array.isArray(response.sites)) {
                    const matchedSite =
                        response.sites.find(
                            (item: SiteSummary & { site_code?: string }) =>
                                item.site_code &&
                                item.site_code.toUpperCase() === normalized
                        ) || response.sites[0];

                    if (matchedSite && isMounted) {
                        setSiteInfo({
                            site_name: matchedSite.site_name,
                            site_code: matchedSite.site_code
                        });
                    }
                }
            } catch (error) {
                // Non-blocking metadata fetch failure; keep fallback display label.
            }
        };

        fetchSiteMeta();

        return () => {
            isMounted = false;
        };
    }, [site]);

    const displaySiteLabel =
        siteInfo?.site_name ||
        siteInfo?.site_code?.toUpperCase() ||
        site?.toUpperCase() ||
        "SITE";

    // Sheet names for Generation Reports submenu
    const reportSheets = [
        { name: "Master Sheet", route: `/entry/${site}?form=Daily Report` },
        { name: "Ambient Form", route: `/entry/${site}?form=Weather` },
        { name: "Databank Form", route: `/entry/${site}?form=Inverters` },
        { name: "BD Details", route: `/entry/${site}?form=Breakdowns` },
        { name: "Meter Reading", route: `/entry/${site}?form=Plant Meters` }
    ];

    // Checksheet names for Documentation submenu
    const documentationSheets = [
        { name: "WMS PM Check Sheet", route: `/documentation/${site}?type=wms-pm` },
        { name: "Inverter Check Sheet", route: `/documentation/${site}?type=inverter` }
    ];

    // Show loading state while validating
    if (isValidating || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-50/30 dark:from-[#0B1120] dark:to-[#0F172A]">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-900 dark:text-slate-100 font-semibold text-lg">Validating user access...</p>
                </div>
            </div>
        );
    }

    const allOptionsList: NavigationOption[] = [
        {
            moduleKey: "dashboard",
            label: "Performance Analytics",
            description: "Live Monitoring and Insights",
            icon: React.createElement(MODULE_ICONS.dashboard, { size: 20 }),
            action: () => navigate(`/dashboard/${site}`),
            color: "dashboard"
        },
        {
            moduleKey: "generation_reports",
            label: "Generation Reports",
            description: "Manual Data Entry & Dashboard",
            icon: React.createElement(MODULE_ICONS.generation_reports, { size: 20 }),
            action: () => navigate(`/entry/${site}`),
            color: "generation_reports"
        },
        {
            moduleKey: "om",
            label: "O&M",
            description: "Activity Tracker & Scheduling",
            icon: React.createElement(MODULE_ICONS.om, { size: 20 }),
            action: () => navigate(`/om/${site}`),
            color: "om"
        },
        {
            moduleKey: "upload",
            label: "Upload Plant Data",
            description: "Integrated Data Repository",
            icon: React.createElement(MODULE_ICONS.upload, { size: 20 }),
            action: () => navigate(`/upload/${site}`),
            color: "upload"
        },
        {
            moduleKey: "alerts",
            label: "Alerts Portal",
            description: "Improve Your MTTR",
            icon: React.createElement(MODULE_ICONS.alerts, { size: 20 }),
            action: () => navigate(`/alerts/${site}`),
            color: "alerts",
        },
        {
            moduleKey: "simulation",
            label: "Simulation-On-Demand",
            description: "Customize Input",
            icon: React.createElement(MODULE_ICONS.simulation, { size: 20 }),
            action: () => navigate(`/simulation/${site}`),
            color: "simulation",
        },
        {
            moduleKey: "dsm",
            label: "DSM",
            description: "Forecasting and Scheduling",
            icon: React.createElement(MODULE_ICONS.dsm, { size: 20 }),
            action: () => navigate(`/dsm/${site}`),
            color: "dsm",
        },
        {
            moduleKey: "inventory",
            label: "Inventory Management",
            description: "Spares' Consumption and Availability",
            icon: React.createElement(MODULE_ICONS.inventory, { size: 20 }),
            action: () => navigate(`/inventory/${site}`),
            color: "inventory"
        },
        {
            moduleKey: "vendors",
            label: "Vendor Management",
            description: "PO and Invoice",
            icon: React.createElement(MODULE_ICONS.vendors, { size: 20 }),
            action: () => navigate(`/vendors/${site}`),
            color: "vendors"
        },
        {
            moduleKey: "documentation",
            label: "Documentation",
            description: "WMS PM check sheets and forms",
            icon: React.createElement(MODULE_ICONS.documentation, { size: 20 }),
            action: () => navigate(`/documentation/${site}`),
            color: "documentation"
        }
    ];

    const getOption = (moduleKey: string) => allOptionsList.find((item) => item.moduleKey === moduleKey) || null;

    const topLeftOption = getOption('dashboard');
    const topRightOption = getOption('generation_reports');
    const middleRowOptions = ['om', 'alerts', 'upload']
        .map((moduleKey) => getOption(moduleKey))
        .filter(Boolean) as NavigationOption[];
    const bottomRowOptions = ['simulation', 'dsm', 'inventory', 'vendors']
        .map((moduleKey) => getOption(moduleKey))
        .filter(Boolean) as NavigationOption[];

    const isLockedModule = (moduleKey: string) => {
        return !hasModuleAccess(moduleKey);
    };
    const hasAnyAccessibleModule = allOptionsList.some((option) => hasModuleAccess(option.moduleKey));
    const showNoModulesAssignedBanner = loadedFromApi && allowedModuleKeys.size === 0;

    const handleModuleClick = (option: NavigationOption) => {
        if (isLockedModule(option.moduleKey)) {
            setLockedModuleWarning(`Module "${option.label}" is locked. Contact your admin support for this feature.`);
            return;
        }
        setLockedModuleWarning(null);
        option.action();
    };


    // ─── Widget Content Renderer ──────────────────────────────────────
    const renderWidgetContent = (label: string) => {
        switch (label) {

            // ── PERFORMANCE ANALYTICS ────────────────────────────────
            case "Performance Analytics": {
                const powerVsRad = generationMetrics.powerVsRad.length > 0
                    ? generationMetrics.powerVsRad
                    : [
                        { time: 'N/A', power: 0, radiation: 0 },
                    ];
                const isLive = generationMetrics.totalDays > 0;
                return (
                    <div className="np-stack">
                        {/* Plant status + counters */}
                        <div className="np-metrics">
                            <div className="np-metric">
                                <span className={`np-status ${isLive ? 'np-status--live' : 'np-status--idle'}`}>
                                    <span className="np-status__dot" />
                                    {isLive ? 'Live' : 'Offline'}
                                </span>
                                <span className="np-metric__label" style={{ marginTop: 6 }}>Plant Status</span>
                            </div>
                            <div className="np-metric">
                                <span className="np-metric__value">{generationMetrics.infoTags}</span>
                                <span className="np-metric__label">Info Tags</span>
                            </div>
                            <div className="np-metric">
                                <span className="np-metric__value">{generationMetrics.totalDays}</span>
                                <span className="np-metric__label">Days of Data</span>
                            </div>
                        </div>

                        {/* Power vs Radiation */}
                        <div className="np-chartbox">
                            <div className="np-seclabel">
                                <span>Power vs Radiation</span>
                                <span className="np-legend">
                                    <span className="np-legend__item">
                                        <span className="np-legend__swatch" style={{ background: '#0084CF' }} />
                                        Power (kW)
                                    </span>
                                    <span className="np-legend__item">
                                        <span className="np-legend__rule" style={{ background: '#0066A3' }} />
                                        Radiation (W/m²)
                                    </span>
                                </span>
                            </div>
                            <div className="np-chartbox">
                                <ChartViewport minHeight={150}>
                                    {({ width, height }) => (
                                        <ComposedChart width={width} height={height} data={powerVsRad} margin={{ top: 6, right: 2, left: -24, bottom: 16 }}>
                                            <defs>
                                                <linearGradient id="powerChartGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#0084CF" stopOpacity={0.85} />
                                                    <stop offset="95%" stopColor="#0084CF" stopOpacity={0.25} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                                            <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" tickLine={false} />
                                            <YAxis yAxisId="left" tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                            <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '3px', border: '1px solid #DFE6EE', boxShadow: '0 6px 20px -8px rgba(28,77,136,.28)' }} />
                                            <Bar yAxisId="left" dataKey="power" name="Power (kW)" fill="url(#powerChartGrad)" radius={[2, 2, 0, 0]} />
                                            <Line yAxisId="right" type="monotone" dataKey="radiation" name="Radiation (W/m²)" stroke="#0066A3" strokeWidth={2} dot={false} />
                                        </ComposedChart>
                                    )}
                                </ChartViewport>
                            </div>
                        </div>
                    </div>
                );
            }
            // ── GENERATION REPORTS ──────────────────────────────────────────
            case "Generation Reports": {
                const gm = generationMetrics;
                const bdChartData = gm.majorBreakdowns || [];
                const bdColors = ['#0084CF', '#1C4D88', '#8AC4E6'];
                return (
                    <div className="np-split">
                        {/* Plant KPIs */}
                        <div className="np-panel">
                            <div className="np-seclabel">Plant KPIs</div>
                            <div className="np-metrics np-metrics--wrap" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                {[
                                    { lbl: 'Total Gen', val: formatMetricValue(gm.totalGeneration, 1, ' MU') },
                                    { lbl: 'Revenue', val: `₹${formatMetricValue(gm.revenue, 1, ' Cr')}` },
                                    { lbl: 'Input (GII)', val: formatMetricValue(gm.insolation, 1, ' kWh/m²') },
                                    { lbl: 'Availability', val: formatMetricValue(gm.pa, 1, '%') },
                                ].map(({ lbl, val }) => (
                                    <div key={lbl} className="np-metric">
                                        <span className="np-metric__value">{val}</span>
                                        <span className="np-metric__label">{lbl}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Major Breakdowns */}
                        <div className="np-panel">
                            <div className="np-seclabel">Major Breakdowns</div>
                            <div className="np-chartbox">
                                {bdChartData.length > 0 ? (
                                    <ChartViewport minHeight={120}>
                                        {({ width, height }) => (
                                            <BarChart width={width} height={height} data={bdChartData} layout="vertical" margin={{ left: -8, right: 6, top: 2, bottom: 8 }}>
                                                <XAxis type="number" tick={{ fontSize: 8, fill: '#6B7A8B' }} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                                <YAxis dataKey="name" type="category" tick={{ fontSize: 8, fill: '#6B7A8B' }} width={62} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                                <Tooltip formatter={(v: any) => `${Number(v).toFixed(0)}`} contentStyle={{ fontSize: '10px', borderRadius: '3px', border: '1px solid #DFE6EE' }} />
                                                <Bar dataKey="value" radius={[0, 2, 2, 0]} maxBarSize={14}>
                                                    {bdChartData.map((_, i) => <Cell key={i} fill={bdColors[i % bdColors.length]} />)}
                                                </Bar>
                                            </BarChart>
                                        )}
                                    </ChartViewport>
                                ) : (
                                    <div className="flex h-full items-center justify-center text-[10px] text-[#6B7A8B]">0</div>
                                )}
                            </div>
                        </div>

                        {/* Controllable Losses */}
                        <div className="np-panel">
                            <div className="np-seclabel">Controllable Losses</div>
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, minHeight: 0 }}>
                                <span className="np-metric__value" style={{ fontSize: 'clamp(15px, 1vw, 19px)' }}>
                                    {formatMetricValue(gm.controllableLosses.value, 2, ' MU')}
                                </span>
                                <div style={{ marginTop: 9, height: 4, borderRadius: 2, background: 'var(--np-line-2)', overflow: 'hidden' }}>
                                    <div
                                        style={{
                                            height: '100%',
                                            width: `${Math.min(gm.controllableLosses.percentage, 100)}%`,
                                            background: '#0084CF',
                                            transition: 'width .5s ease',
                                        }}
                                    />
                                </div>
                                <span className="np-metric__label" style={{ marginTop: 7 }}>
                                    {gm.controllableLosses.percentage.toFixed(1)}% of total gen
                                </span>
                            </div>
                        </div>

                        {/* Uncontrollable Losses */}
                        <div className="np-panel">
                            <div className="np-seclabel">Uncontrollable Losses</div>
                            <div className="np-params" style={{ flex: 1, minHeight: 0, alignContent: 'center' }}>
                                <div className="np-param">
                                    <span className="np-param__label">Input Loss</span>
                                    <span className="np-param__value">{formatMetricValue(gm.uncontrollableLosses.weather, 2, ' MU')}</span>
                                </div>
                                <div className="np-param">
                                    <span className="np-param__label">GA Loss</span>
                                    <span className="np-param__value">{formatMetricValue(gm.gaLoss, 2, ' MU')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            // ── O&M ────────────────────────────────────────────────────────────
            case "O&M": {
                const pendingActivities = omMetrics.pendingActivities.length > 0
                    ? omMetrics.pendingActivities
                    : [{ name: 'No pending activity', date: 'N/A' }];
                return (
                    <div className="np-stack">
                        <div className="np-metrics">
                            {[
                                { lbl: 'Completed (Last Wk)', val: omMetrics.completedLastWeek },
                                { lbl: 'Scheduled (Next Wk)', val: omMetrics.scheduledNextWeek },
                                { lbl: 'PTWs Closed', val: omMetrics.ptwClosed },
                            ].map(({ lbl, val }) => (
                                <div key={lbl} className="np-metric">
                                    <span className="np-metric__value">{val}</span>
                                    <span className="np-metric__label">{lbl}</span>
                                </div>
                            ))}
                        </div>

                        <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <div className="np-seclabel">Pending Activities</div>
                            <div className="np-params" style={{ overflow: 'hidden' }}>
                                {pendingActivities.map((a, i) => (
                                    <div key={i} className="np-param">
                                        <span className="np-param__label" style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#0084CF', flex: 'none' }} />
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                                        </span>
                                        <span className="np-param__value" style={{ fontWeight: 400 }}>{a.date}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="np-param" style={{ borderTop: '1px solid var(--np-line-2)', borderBottom: 0, paddingTop: 9, flex: 'none' }}>
                            <span className="np-param__label">Total Checklists Available</span>
                            <span className="np-param__value">{omMetrics.totalChecklists}</span>
                        </div>
                    </div>
                );
            }

            // ── UPLOAD PLANT DATA ──────────────────────────────────────────
            case "Upload Plant Data": {
                return (
                    <div className="np-stack">
                        <div
                            className="np-dropzone"
                            style={{ flex: '1 1 auto', minHeight: 0 }}
                        >
                            <span className="np-dropzone__icon">
                                <UploadCloud size={22} strokeWidth={1.7} />
                            </span>
                            <p className="np-dropzone__title">Drag &amp; Drop Plant Files</p>
                            <p className="np-dropzone__hint">CSV, XLSX formats supported</p>
                        </div>
                    </div>
                );
            }

            // ── ALERTS PORTAL ──────────────────────────────────────────────
            case "Alerts Portal": {
                const categories = [
                    { lbl: 'High',       count: alertsMetrics.high },
                    { lbl: 'Medium',     count: alertsMetrics.medium },
                    { lbl: 'Low',        count: alertsMetrics.low },
                    { lbl: 'Accurate',   count: alertsMetrics.accurate },
                    { lbl: 'Inaccurate', count: alertsMetrics.inaccurate },
                    { lbl: 'Pending',    count: alertsMetrics.pending },
                ];
                const alertTimelineData = alertsMetrics.timeline;
                return (
                    <div className="np-stack">
                        <div className="np-metrics np-metrics--wrap" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                            {categories.map(({ lbl, count }) => (
                                <div key={lbl} className="np-metric">
                                    <span className="np-metric__value">{count}</span>
                                    <span className="np-metric__label">{lbl}</span>
                                </div>
                            ))}
                        </div>
                        <div className="np-chartbox">
                            <div className="np-seclabel">Alert Timeline</div>
                            <div className="np-chartbox">
                                <ChartViewport minHeight={120}>
                                    {({ width, height }) => (
                                        <AreaChart width={width} height={height} data={alertTimelineData} margin={{ top: 6, right: 2, left: -38, bottom: 14 }}>
                                            <defs>
                                                <linearGradient id="alertGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#0084CF" stopOpacity={0.35} />
                                                    <stop offset="95%" stopColor="#0084CF" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                                            <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" tickLine={false} />
                                            <YAxis tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                            <Tooltip contentStyle={{ fontSize: '10px', borderRadius: '3px', border: '1px solid #DFE6EE' }} />
                                            <Area type="monotone" dataKey="alerts" stroke="#0084CF" fill="url(#alertGrad)" strokeWidth={2} />
                                        </AreaChart>
                                    )}
                                </ChartViewport>
                            </div>
                        </div>
                    </div>
                );
            }

            // ── SIMULATION-ON-DEMAND ──────────────────────────────────────────
            case "Simulation-On-Demand": {
                const features = [
                    { title: 'Raw Data Access', desc: 'Direct access to SCADA & meter data' },
                    { title: 'Instant Alerts', desc: 'Real-time alert notifications' },
                    { title: 'Power Optimization', desc: 'ML-based scenario optimization' },
                ];
                const rtcDemoData = [
                    { hour: '00', Grid_MW: 10, Exchange_MW: 5, Batt_C_D_MW: 0, Solar_Power_MW: 0, Wind_Power_MW: 15 },
                    { hour: '06', Grid_MW: 15, Exchange_MW: 10, Batt_C_D_MW: -5, Solar_Power_MW: 10, Wind_Power_MW: 20 },
                    { hour: '12', Grid_MW: 5, Exchange_MW: 15, Batt_C_D_MW: 20, Solar_Power_MW: 85, Wind_Power_MW: 10 },
                    { hour: '18', Grid_MW: 20, Exchange_MW: -5, Batt_C_D_MW: 10, Solar_Power_MW: 15, Wind_Power_MW: 25 },
                    { hour: '23', Grid_MW: 15, Exchange_MW: 0, Batt_C_D_MW: 5, Solar_Power_MW: 0, Wind_Power_MW: 20 },
                ];
                return (
                    <div className="np-stack">
                        <div className="np-params" style={{ flex: 'none' }}>
                            {features.map(({ title, desc }) => (
                                <div key={title} className="np-param" style={{ alignItems: 'center' }}>
                                    <span className="np-param__value" style={{ flex: '0 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {title}
                                    </span>
                                    <span className="np-param__label" style={{ textAlign: 'right' }}>{desc}</span>
                                </div>
                            ))}
                        </div>
                        <div className="np-chartbox">
                            <div className="np-seclabel">Scenario Optimization (kW)</div>
                            <div className="np-chartbox">
                                <ChartViewport minHeight={120}>
                                    {({ width, height }) => (
                                        <AreaChart width={width} height={height} data={rtcGraphData.length > 0 ? rtcGraphData : rtcDemoData} margin={{ top: 6, right: 2, left: -36, bottom: 14 }}>
                                            <defs>
                                                <linearGradient id="colorSolarCard" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0084CF" stopOpacity={0.6}/><stop offset="95%" stopColor="#0084CF" stopOpacity={0.1}/></linearGradient>
                                                <linearGradient id="colorWindCard" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0084CF" stopOpacity={0.6}/><stop offset="95%" stopColor="#0084CF" stopOpacity={0.1}/></linearGradient>
                                                <linearGradient id="colorGridCard" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8AC4E6" stopOpacity={0.6}/><stop offset="95%" stopColor="#8AC4E6" stopOpacity={0.1}/></linearGradient>
                                                <linearGradient id="colorExchangeCard" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0D2747" stopOpacity={0.6}/><stop offset="95%" stopColor="#0D2747" stopOpacity={0.1}/></linearGradient>
                                                <linearGradient id="colorBatteryCard" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3EA3DA" stopOpacity={0.6}/><stop offset="95%" stopColor="#3EA3DA" stopOpacity={0.1}/></linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                                            <XAxis dataKey="hour" tick={{ fontSize: 8, fill: '#6B7A8B' }} stroke="#DFE6EE" tickLine={false} />
                                            <YAxis tick={{ fontSize: 8, fill: '#6B7A8B' }} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                            <ReferenceLine y={0} stroke="#C5D0DC" strokeDasharray="3 3" />
                                            <Tooltip contentStyle={{ fontSize: '9px', borderRadius: '3px', border: '1px solid #DFE6EE' }} />
                                            <Area type="linear" dataKey="Grid_MW" name="Grid" stroke="#8AC4E6" strokeWidth={2} fill="none" connectNulls />
                                            <Area type="linear" dataKey="Exchange_MW" name="Exchange" stroke="#0D2747" strokeWidth={1} fill="none" connectNulls />
                                            <Area type="linear" dataKey="Batt_C_D_MW" name="Battery" stroke="#1C4D88" strokeWidth={1} fill="none" connectNulls />
                                            <Area type="linear" dataKey="Solar_Power_MW" name="Solar" stroke="#0084CF" strokeWidth={2} fill="none" connectNulls />
                                            <Area type="linear" dataKey="Wind_Power_MW" name="Wind" stroke="#0066A3" strokeWidth={2} fill="none" connectNulls />
                                        </AreaChart>
                                    )}
                                </ChartViewport>
                            </div>
                        </div>
                    </div>
                );
            }

            // ── DSM ────────────────────────────────────────────────────────────
            case "DSM": {
                const dsmFeatures = [
                    { title: 'Grid Compliance', desc: 'Maintain SLDC/RLDC schedule adherence' },
                    { title: 'Load Management', desc: 'Optimise injection vs drawl scheduling' },
                    { title: 'Physical & ML Forecasting', desc: 'Dual-mode day-ahead generation forecast' },
                ];
                return (
                    <div className="np-stack" style={{ justifyContent: 'center' }}>
                        {dsmFeatures.map(({ title, desc }) => (
                            <div key={title} className="np-feature">
                                <span className="np-feature__rule" />
                                <span style={{ minWidth: 0 }}>
                                    <span className="np-feature__title">{title}</span>
                                    <span className="np-feature__desc">{desc}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                );
            }

            // ── INVENTORY MANAGEMENT ──────────────────────────────────────
            case "Inventory Management": {
                const stats = [
                    { lbl: 'Total Items',  val: `${inventoryMetrics.totalItems}` },
                    { lbl: 'Out-of-Stock', val: `${inventoryMetrics.outOfStock}` },
                    { lbl: 'Highest (3M)', val: inventoryMetrics.highest3m },
                    { lbl: 'Least (3M)',   val: inventoryMetrics.least3m },
                ];
                const invChart = inventoryMetrics.chart;
                return (
                    <div className="np-stack">
                        <div className="np-metrics np-metrics--wrap" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                            {stats.map(({ lbl, val }) => (
                                <div key={lbl} className="np-metric">
                                    <span className="np-metric__value">{val}</span>
                                    <span className="np-metric__label">{lbl}</span>
                                </div>
                            ))}
                        </div>
                        <div className="np-chartbox">
                            <div className="np-seclabel">Stock by Category</div>
                            <div className="np-chartbox">
                                <ChartViewport minHeight={120}>
                                    {({ width, height }) => (
                                        <BarChart width={width} height={height} data={invChart} margin={{ top: 6, right: 2, left: -24, bottom: 14 }}>
                                            <defs>
                                                <linearGradient id="invChartGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#0084CF" stopOpacity={0.85} />
                                                    <stop offset="95%" stopColor="#0084CF" stopOpacity={0.25} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                                            <XAxis dataKey="cat" tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" tickLine={false} />
                                            <YAxis tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                            <Tooltip contentStyle={{ fontSize: '10px', borderRadius: '3px', border: '1px solid #DFE6EE' }} />
                                            <Bar dataKey="stock" fill="url(#invChartGrad)" radius={[2, 2, 0, 0]} maxBarSize={30} />
                                        </BarChart>
                                    )}
                                </ChartViewport>
                            </div>
                        </div>
                    </div>
                );
            }

            // ── VENDOR MANAGEMENT ──────────────────────────────────────────
            case "Vendor Management": {
                const vendorStats = [
                    { lbl: 'Registered Vendors', val: `${vendorMetrics.registeredVendors}` },
                    { lbl: 'POs Issued',         val: `${vendorMetrics.posIssued}` },
                    { lbl: 'Total PO Value',     val: `₹${vendorMetrics.totalPoValueCr.toFixed(1)} Cr` },
                    { lbl: 'Amount Due',         val: `₹${vendorMetrics.amountDueCr.toFixed(1)} Cr` },
                ];
                const vendorChart = vendorMetrics.performance;
                return (
                    <div className="np-stack">
                        <div className="np-metrics np-metrics--wrap" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                            {vendorStats.map(({ lbl, val }) => (
                                <div key={lbl} className="np-metric">
                                    <span className="np-metric__value">{val}</span>
                                    <span className="np-metric__label">{lbl}</span>
                                </div>
                            ))}
                        </div>
                        <div className="np-chartbox">
                            <div className="np-seclabel">Vendor Performance</div>
                            <div className="np-chartbox">
                                <ChartViewport minHeight={120}>
                                    {({ width, height }) => (
                                        <BarChart width={width} height={height} data={vendorChart} margin={{ top: 6, right: 2, left: -24, bottom: 14 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                                            <XAxis dataKey="vendor" tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" tickLine={false} />
                                            <YAxis domain={[70, 100]} tick={{ fontSize: 9, fill: '#6B7A8B' }} stroke="#DFE6EE" axisLine={false} tickLine={false} />
                                            <Tooltip formatter={(v: any) => `${v}%`} contentStyle={{ fontSize: '10px', borderRadius: '3px', border: '1px solid #DFE6EE' }} />
                                            <Bar dataKey="perf" fill="#0084CF" radius={[2, 2, 0, 0]} maxBarSize={30} />
                                        </BarChart>
                                    )}
                                </ChartViewport>
                            </div>
                        </div>
                    </div>
                );
            }

            // ── DEFAULT ────────────────────────────────────────────────────
            default:
                return (
                    <div className="np-dropzone" style={{ flex: '1 1 auto', minHeight: 0, borderStyle: 'solid' }}>
                        <span className="np-dropzone__icon">
                            <BookOpen size={22} strokeWidth={1.7} />
                        </span>
                        <p className="np-dropzone__title">View Documentation Portal</p>
                        <p className="np-dropzone__hint" style={{ maxWidth: 240 }}>
                            Access manuals, checklists, and compliance documents on-demand.
                        </p>
                    </div>
                );
        }
    };


    // Module access skeleton grid shown while permissions are loading
    if (moduleAccessLoading) {
        return (
            <div className="navigation-page min-h-screen bg-gradient-to-br from-slate-50 via-slate-50/30 to-slate-50 flex">
                <div className="flex-1 w-full overflow-hidden">
                    <div className="h-screen pt-20 pb-4 px-6">
                        <div className="max-w-[1850px] mx-auto w-full h-full grid grid-rows-3 gap-2 animate-pulse">
                            <div className="grid grid-cols-12 gap-2">
                                <div className="col-span-12 lg:col-span-5 bg-slate-200/60 rounded-2xl" />
                                <div className="col-span-12 lg:col-span-7 bg-slate-200/60 rounded-2xl" />
                            </div>
                            <div className="grid grid-cols-12 gap-2">
                                <div className="col-span-12 md:col-span-4 bg-slate-200/60 rounded-2xl" />
                                <div className="col-span-12 md:col-span-4 bg-slate-200/60 rounded-2xl" />
                                <div className="col-span-12 md:col-span-4 bg-slate-200/60 rounded-2xl" />
                            </div>
                            <div className="grid grid-cols-12 gap-2">
                                <div className="col-span-6 md:col-span-3 bg-slate-200/60 rounded-2xl" />
                                <div className="col-span-6 md:col-span-3 bg-slate-200/60 rounded-2xl" />
                                <div className="col-span-6 md:col-span-3 bg-slate-200/60 rounded-2xl" />
                                <div className="col-span-6 md:col-span-3 bg-slate-200/60 rounded-2xl" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className="navigation-page min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-[#0B1120] dark:via-[#0F172A] dark:to-[#0B1120] text-slate-800 dark:text-slate-100 flex">
            {/* Main Content */}
            <div className="flex-1 w-full overflow-y-auto">
                <div className="pt-[88px] pb-[clamp(24px,3vw,56px)]">
                    <div className="np-shell">

                        {showNoModulesAssignedBanner && (
                            <div className="np-notice">
                                <AlertTriangle size={14} className="text-[#0066A3] flex-shrink-0" />
                                <p>
                                    No modules are assigned to your role/site. All module cards are frozen. Contact your admin to assign modules.
                                </p>
                            </div>
                        )}

                        {lockedModuleWarning && (
                            <div className="np-notice" style={{ justifyContent: 'space-between' }}>
                                <div className="flex items-center gap-2">
                                    <AlertTriangle size={14} className="text-[#0066A3] flex-shrink-0" />
                                    <p>{lockedModuleWarning}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setLockedModuleWarning(null)}
                                    className="text-[#0084CF] hover:text-[#1C4D88] transition-colors"
                                    aria-label="Dismiss warning"
                                >
                                    <X size={13} />
                                </button>
                            </div>
                        )}

                        {!hasAnyAccessibleModule && !showNoModulesAssignedBanner && loadedFromApi && (
                            <div className="np-notice">
                                <AlertTriangle size={14} className="text-[#0066A3] flex-shrink-0" />
                                <p>
                                    Module access is currently restricted for this site.
                                </p>
                            </div>
                        )}

                        <div className="np-notice np-notice--guide">
                            <BookOpen size={14} className="text-[#0066A3] flex-shrink-0" aria-hidden="true" />
                            <p>New here? Take a 30-second tour, or open the full guide.</p>
                            <button
                                type="button"
                                onClick={() => navigate(`/navigate/${site}?tour=hub`)}
                                className="np-notice__action np-notice__action--primary"
                            >
                                Take the tour
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate(`/guide/${site}`)}
                                className="np-notice__action"
                            >
                                Full guide <ChevronRight size={13} aria-hidden="true" />
                            </button>
                        </div>

                        {/* ── Row 1 ── Analytics Dashboard + Generation Reports (5:7 emphasis) */}
                        <div className="np-row--feature">
                            {topLeftOption && (
                                <CardShell
                                    option={topLeftOption}
                                    colSpan=""
                                    className="np-card--feature"
                                    delay={0.05}
                                    hoverIdx={0}
                                    locked={isLockedModule(topLeftOption.moduleKey)}
                                    onClick={() => handleModuleClick(topLeftOption)}
                                >
                                    {renderWidgetContent(topLeftOption.label)}
                                </CardShell>
                            )}

                            {topRightOption && (
                                <CardShell
                                    option={topRightOption}
                                    colSpan=""
                                    className="np-card--feature"
                                    delay={0.12}
                                    hoverIdx={1}
                                    locked={isLockedModule(topRightOption.moduleKey)}
                                    onClick={() => handleModuleClick(topRightOption)}
                                >
                                    {/* date range badge injected here */}
                                    {generationMetrics.dateRange && (
                                        <div className="mb-1 flex">
                                            <span className="text-[9px] font-medium text-slate-500 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 px-1.5 py-0.5 rounded-md">
                                                {generationMetrics.dateRange}
                                            </span>
                                        </div>
                                    )}
                                    {renderWidgetContent(topRightOption.label)}
                                </CardShell>
                            )}
                        </div>

                        {/* ── Row 2 ── O&M + Alerts + Upload Plant Data */}
                        <div className="np-row--modules">
                            {middleRowOptions.map((option, idx) => (
                                <CardShell
                                    key={option.moduleKey}
                                    option={option}
                                    colSpan=""
                                    className="np-card--compact"
                                    delay={0.2 + idx * 0.07}
                                    hoverIdx={10 + idx}
                                    locked={isLockedModule(option.moduleKey)}
                                    onClick={() => handleModuleClick(option)}
                                >
                                    {renderWidgetContent(option.label)}
                                </CardShell>
                            ))}
                        </div>

                        {/* ── Row 3 ── Simulation + DSM + Inventory + Vendor */}
                        <div className="np-row--modules">
                            {bottomRowOptions.map((option, idx) => (
                                <CardShell
                                    key={option.moduleKey}
                                    option={option}
                                    colSpan=""
                                    className="np-card--compact"
                                    delay={0.35 + idx * 0.07}
                                    hoverIdx={20 + idx}
                                    locked={isLockedModule(option.moduleKey)}
                                    onClick={() => handleModuleClick(option)}
                                >
                                    {renderWidgetContent(option.label)}
                                </CardShell>
                            ))}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default NavigationPage;


